import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, ImagePlus, Lock, Check, FileText, Mic, Square, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fileToDataUrl } from "../lib/image";
import Header from "../components/Header";
import Modal from "../components/Modal";

const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function NotesPage() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { user, canUsePro } = useAuth();
  const [profile, setProfile] = useState(null);
  const [notes, setNotes] = useState([]);
  const [modal, setModal] = useState(null); // null | { text, photos, noteId? }
  const [recording, setRecording] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [playing, setPlaying] = useState(null); // note id currently loading/playing
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const [p, n] = await Promise.all([api.get("/profiles"), api.get(`/profiles/${pid}/notes`)]);
      setProfile(p.data.find((x) => x.id === pid) || null);
      setNotes(n.data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/app");
    }
  }, [pid, navigate]);

  useEffect(() => { load(); }, [load]);

  const canModify = (note) => note.created_by === user?.id || user?.org_role === "owner";

  const saveNote = async () => {
    if (!modal.text.trim()) return toast.error("Write the note first");
    try {
      if (modal.noteId) await api.patch(`/notes/${modal.noteId}`, { text: modal.text.trim(), photos: modal.photos });
      else await api.post(`/profiles/${pid}/notes`, { text: modal.text.trim(), photos: modal.photos });
      setModal(null);
      await load();
      toast.success("Note saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      setNotes((n) => n.filter((x) => x.id !== id));
      toast.success("Note deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const addPhoto = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file, 512);
      setModal((m) => ({ ...m, photos: [...m.photos, dataUrl].slice(0, 3) }));
    } catch {
      toast.error("Could not read that photo");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setUploadingAudio(true);
        try {
          const form = new FormData();
          form.append("file", blob, `voicenote.${(rec.mimeType || "audio/webm").includes("mp4") ? "m4a" : "webm"}`);
          await api.post(`/profiles/${pid}/notes/audio`, form, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success("Voice note saved");
          await load();
        } catch (e) {
          toast.error(apiError(e));
        } finally {
          setUploadingAudio(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone not available — allow mic access to record voice notes");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const playVoiceNote = async (noteId) => {
    try {
      setPlaying(noteId);
      const res = await api.get(`/notes/${noteId}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlaying(null); URL.revokeObjectURL(url); };
      await audio.play();
    } catch (e) {
      setPlaying(null);
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="notes-page" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8 pb-24">
        <button data-testid="back-button" onClick={() => navigate("/app")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FileText size={32} strokeWidth={2.5} className="text-[#9F7AEA]" />
            <h1 className="text-3xl font-extrabold tracking-tight">Care notes{profile ? ` — ${profile.name}` : ""}</h1>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="record-voice-note-button"
              onClick={recording ? stopRecording : startRecording}
              disabled={uploadingAudio}
              aria-pressed={recording}
              className={`min-h-[56px] px-6 rounded-full font-bold flex items-center gap-2 transition-colors duration-200 ${recording ? "bg-[#C53030] text-white animate-pulse" : "border-2 border-foreground/25 bg-white hover:bg-secondary"}`}
            >
              {uploadingAudio ? <><Loader2 size={22} className="animate-spin" /> Saving…</> : recording ? <><Square size={20} strokeWidth={2.5} /> Stop</> : <><Mic size={22} strokeWidth={2.5} /> Voice note</>}
            </button>
            <button
              data-testid="add-note-button"
              onClick={() => setModal({ text: "", photos: [] })}
              className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
            >
              <Plus size={22} strokeWidth={3} /> Add note
            </button>
          </div>
        </div>
        <p className="mt-2 text-lg text-muted-foreground">Every note records who wrote it and when.</p>

        <div className="mt-6 space-y-4">
          {notes.length === 0 && (
            <p data-testid="no-notes-message" className="bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
              No notes yet. Add the first one — e.g. mood, meals, medication, incidents.
            </p>
          )}
          {notes.map((n) => (
            <div key={n.id} data-testid={`note-card-${n.id}`} className="bg-white rounded-3xl border-2 border-foreground/10 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-base font-extrabold">{n.author_name}</p>
                  <p className="text-sm font-semibold text-muted-foreground">
                    {fmtDate(n.created_at)}
                    {n.updated_by ? ` · edited by ${n.updated_by}` : ""}
                  </p>
                </div>
                {canModify(n) && (
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`edit-note-${n.id}`} onClick={() => setModal({ text: n.text, photos: n.photos || [], noteId: n.id })} aria-label="Edit note" className="min-w-[44px] min-h-[44px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200">
                      <Pencil size={18} strokeWidth={2.5} />
                    </button>
                    <button data-testid={`delete-note-${n.id}`} onClick={() => deleteNote(n.id)} aria-label="Delete note" className="min-w-[44px] min-h-[44px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200">
                      <Trash2 size={18} strokeWidth={2.5} />
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-3 text-lg leading-relaxed whitespace-pre-wrap">{n.text}</p>
              {n.has_audio && (
                <button
                  data-testid={`play-voice-note-${n.id}`}
                  onClick={() => playVoiceNote(n.id)}
                  aria-label={`Play voice note by ${n.author_name}`}
                  className="mt-3 min-h-[56px] px-6 rounded-full bg-primary/10 border-2 border-primary/40 text-primary font-bold flex items-center gap-2 hover:bg-primary/20 transition-colors duration-200"
                >
                  {playing === n.id ? <Loader2 size={22} className="animate-spin" /> : <Play size={22} strokeWidth={2.5} />} Play voice note
                </button>
              )}
              {n.photos?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {n.photos.map((src, i) => (
                    <img key={i} src={src} alt={`Note attachment ${i + 1}`} data-testid={`note-photo-${n.id}-${i}`} className="w-28 h-28 rounded-2xl object-cover border-2 border-foreground/15" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>

      {modal && (
        <Modal title={modal.noteId ? "Edit note" : "Add a care note"} onClose={() => setModal(null)} testId="note-modal">
          <label htmlFor="note-text" className="block text-base font-bold mb-1">Note</label>
          <textarea
            id="note-text"
            data-testid="note-text-input"
            value={modal.text}
            onChange={(e) => setModal((m) => ({ ...m, text: e.target.value }))}
            rows={4}
            placeholder="e.g. Ate all of lunch, cheerful after music time"
            className="w-full rounded-2xl border-2 border-foreground/25 px-4 py-3 text-lg focus:outline-none focus:border-primary"
          />
          <p className="mt-4 mb-2 text-base font-bold">Photos {!canUsePro && <span className="text-[#B7791F]">(Pro)</span>}</p>
          {canUsePro ? (
            <div className="flex flex-wrap items-center gap-3">
              <input type="file" accept="image/*" className="hidden" ref={fileInput} onChange={(e) => addPhoto(e.target.files[0])} />
              <button data-testid="note-photo-upload-button" onClick={() => fileInput.current?.click()} disabled={modal.photos.length >= 3} className="min-h-[56px] px-5 rounded-2xl border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200 disabled:opacity-50">
                <ImagePlus size={22} strokeWidth={2.5} /> Add photo ({modal.photos.length}/3)
              </button>
              {modal.photos.map((src, i) => (
                <button key={i} onClick={() => setModal((m) => ({ ...m, photos: m.photos.filter((_, x) => x !== i) }))} aria-label="Remove photo" className="relative">
                  <img src={src} alt="" className="w-16 h-16 rounded-2xl object-cover border-2 border-foreground/15" />
                </button>
              ))}
            </div>
          ) : (
            <button data-testid="note-photo-locked-button" onClick={() => navigate("/premium")} className="min-h-[56px] px-5 rounded-2xl border-2 border-dashed border-foreground/25 bg-secondary/50 font-bold flex items-center gap-2 text-muted-foreground">
              <Lock size={22} strokeWidth={2.5} /> Unlock photo notes with Premium
            </button>
          )}
          <button
            data-testid="save-note-button"
            onClick={saveNote}
            className="mt-6 w-full min-h-[56px] rounded-full bg-primary text-white text-lg font-bold flex items-center justify-center gap-2"
          >
            <Check size={24} strokeWidth={3} /> Save note
          </button>
        </Modal>
      )}
    </div>
  );
}
